import React from 'react';
import { z } from 'zod';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { X } from 'lucide-react';
import { Controller, useFormContext } from 'react-hook-form';
import { normalizeAnswerText } from '@/hooks/submission_forms/questionVisibility';
import { Option, QuestionData } from '../types';

/**
 * A multi-select answer is a list of the selected options. Older OV2 transfers
 * stored a single choice as plain text ("Industry Sponsored"); read that as a
 * one-item list so it can't be spread into letters or substring-matched
 * (OV-1519). '' (no answer / cleared hidden field) reads as nothing selected.
 *
 * Entries are kept exactly as saved: the save diff compares against what the
 * server holds, so rewriting them here would change the stored answer on the
 * next autosave without the user touching the question (OV-1527).
 */
const toEntries = (value: unknown): unknown[] => {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string' && value !== '') return [value];
  return [];
};

// The answer is unvalidated jsonb: a null or blank entry carries no answer.
const isBlank = (entry: unknown): boolean =>
  entry === null || entry === undefined || String(entry).trim() === '';

// Same comparison the follow-up visibility uses, so OV2's "Yes" ticks the
// option whose value is "yes" rather than being listed as unmatched.
const matchesOption = (entry: unknown, option: Option): boolean =>
  !isBlank(entry) &&
  normalizeAnswerText(entry) === normalizeAnswerText(option.value);

// A non-string entry is shown as its JSON; rendering it raw would crash.
const entryText = (entry: unknown): string =>
  typeof entry === 'string' ? entry : JSON.stringify(entry);

interface SelectMultiFieldProps {
  question: QuestionData;
  placeholder?: string;
  disabled?: boolean;
}

const SelectMultiFieldComponent: React.FC<SelectMultiFieldProps> = ({
  question,
  disabled,
}) => {
  const { control, trigger } = useFormContext();
  if (!question.options) {
    return (
      <Input
        placeholder="No options available"
        className="w-full"
        disabled={disabled}
      />
    );
  }
  const options = question.options;

  return (
    <Controller
      name={question.slug}
      control={control}
      defaultValue={[]}
      render={({ field: { value, onChange } }) => {
        const entries = toEntries(value);
        // Once the user edits the question its blank entries are dropped.
        const update = (next: unknown[]) => {
          onChange(next.filter(entry => !isBlank(entry)));
          trigger(question.slug);
        };
        // A stored value that matches no option (OV2 saved several choices
        // as one text, "Drug, Device", or the option was reworded later) has
        // no checkbox to show it. List it so it is never hidden, and let the
        // user remove it explicitly; it is never dropped silently (OV-1527).
        const unmatched = entries
          .map((entry, index) => ({ entry, index }))
          .filter(
            ({ entry }) =>
              !isBlank(entry) &&
              !options.some(option => matchesOption(entry, option))
          );
        return (
          <div className="space-y-2">
            {options.map(option => {
              const isChecked = entries.some(entry =>
                matchesOption(entry, option)
              );
              return (
                <div key={option.value} className="flex items-center space-x-2">
                  <Checkbox
                    checked={isChecked}
                    onCheckedChange={checked =>
                      update(
                        checked
                          ? [...entries, option.value]
                          : entries.filter(
                              entry => !matchesOption(entry, option)
                            )
                      )
                    }
                    id={`${question.id}-${option.value}`}
                    disabled={disabled}
                  />
                  <label
                    htmlFor={`${question.id}-${option.value}`}
                    className="text-sm text-gray-700 cursor-pointer"
                  >
                    {option.label}
                  </label>
                </div>
              );
            })}
            {unmatched.map(({ entry, index }) => {
              const text = entryText(entry);
              return (
                <div
                  key={index}
                  data-testid={`${question.slug}-previous-answer`}
                  className="flex items-center gap-2 text-sm text-muted-foreground"
                >
                  <span>Previously answered: {text}</span>
                  {!disabled && (
                    <button
                      type="button"
                      onClick={() =>
                        update(entries.filter((_, i) => i !== index))
                      }
                      className="print:hidden text-muted-foreground hover:text-destructive"
                      aria-label={`Remove previous answer: ${text}`}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        );
      }}
    />
  );
};

const createSelectMultiSchema = (question: QuestionData): z.ZodTypeAny => {
  if (question.required) {
    // Blank entries are hidden, so they must not count as an answer.
    return z
      .array(z.unknown())
      .refine(entries => entries.some(entry => !isBlank(entry)), {
        message: 'This field is required',
      });
  }
  // Accept '' as valid — FormField clears hidden conditional fields to ''
  return z.union([z.array(z.unknown()), z.literal('')]);
};

const getDefaultValue = (): string[] => [];

export const SelectMultiField = {
  component: SelectMultiFieldComponent,
  createSchema: createSelectMultiSchema,
  getDefaultValue: () => getDefaultValue(),
  // A plain-text saved answer seeds as a one-item list, so the checkboxes,
  // validation and follow-up visibility all see a list. '' (no answer) is kept.
  normalizeSavedValue: (value: unknown) =>
    value === '' ? value : toEntries(value),
};
